import { Box, Grid, useMediaQuery, useTheme } from "@mui/material"
import fp from "lodash/fp"
import { memo, useMemo } from "react"

import type {
  PositionFieldMapping,
  PowerlineDevicePositionPlotValues,
} from "types/devicePosition.types"
import type { EntityType } from "types/common.types"

import { GRID_LAYOUT_GAP, SM_ROW_HEIGHT, MD_ROW_HEIGHT } from "helpers/utils/constants"
import Plot from "widgets/plots/Plot"
import useDateRange from "helpers/hooks/useDateRange"
import { formatTelemetryData } from "widgets/plots/helpers"
import PowerlineDevicePositionPlot from "widgets/plots/device/devicePosition/PowerlineDevicePositionPlot"
import { useMultiDeviceTelemetry } from "helpers/hooks/plots/useMultiDeviceTelemetry"
import {
  alphaAmplitudePlotDef,
  betaAmplitudePlotDef,
  alphaDriftPlotDef,
  betaDriftPlotDef,
  amplitudeReqConfig,
  positionInPowerlinePlotDef,
} from "helpers/config/dashboards"
import { getAnglesDrift, getLatestAngles } from "helpers/utils/devicePosition"
import { useDashboardContext } from "helpers/hooks/useDashboardContext"
import { MOTION_ALPHA_FIELD, MOTION_BETA_FIELD } from "helpers/config/telemetryFieldDefs"

const PlotMemo = memo(Plot)

const POWERLINE_POSITION_FIELDS: PositionFieldMapping = {
  first: MOTION_ALPHA_FIELD,
  second: MOTION_BETA_FIELD,
}

interface PowerlineDevicePositionProps {
  deviceId: number | string
  deviceIds: number[]
}

const PowerlineDevicePosition = ({
  deviceId,
  deviceIds,
}: PowerlineDevicePositionProps) => {
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.only("sm"))
  const { rowHeight: contextRowHeight } = useDashboardContext()
  const rowHeight = contextRowHeight ?? (isSmall ? SM_ROW_HEIGHT : MD_ROW_HEIGHT)

  const { getDateRangeTools } = useDateRange()
  const { dateRange, isDateRangeValid } = useMemo(
    () => getDateRangeTools("device"),
    [getDateRangeTools],
  )

  const {
    data,
    isFetching: isPositionLoading,
    qualityData,
    isRawData,
  } = useMultiDeviceTelemetry({
    deviceIds,
    reqConfig: positionInPowerlinePlotDef.reqConfig,
    dateRange,
    isDateRangeValid,
  })

  const { data: amplitudeData } = useMultiDeviceTelemetry({
    deviceIds,
    reqConfig: amplitudeReqConfig,
    dateRange,
    isDateRangeValid,
  })

  const formattedData = useMemo(() => formatTelemetryData(data), [data])

  const latestData = useMemo(
    () => getLatestAngles(data, POWERLINE_POSITION_FIELDS),
    [data],
  )

  const positionPlotValues = useMemo<PowerlineDevicePositionPlotValues>(
    () => ({
      _time: latestData._time,
      alpha: latestData.first,
      beta: latestData.second,
    }),
    [latestData],
  )

  const alphaAmplitude = useMemo(
    () => fp.find({ _field: POWERLINE_POSITION_FIELDS.first }, amplitudeData),
    [amplitudeData],
  )

  const betaAmplitude = useMemo(
    () => fp.find({ _field: POWERLINE_POSITION_FIELDS.second }, amplitudeData),
    [amplitudeData],
  )

  const { first: alphaDrift, second: betaDrift } = useMemo(
    () => getAnglesDrift(data, POWERLINE_POSITION_FIELDS),
    [data],
  )

  const csvData = useMemo(
    (): {
      entity: EntityType
      entityIds: number[]
      fromDate: string
      toDate: string
    } => ({
      entity: "device",
      entityIds: [Number(deviceId)],
      fromDate: String(dateRange?.fromDate),
      toDate: String(dateRange?.toDate),
    }),
    [deviceId, dateRange?.fromDate, dateRange?.toDate],
  )

  if (!data.length) {
    return <></>
  }

  return (
    <>
      <Box height={"100%"} display={"flex"} flexDirection={"column"}>
        {Number.isFinite(latestData._time) && (
          <PowerlineDevicePositionPlot
            values={positionPlotValues}
            isLoading={isPositionLoading}
          />
        )}
        <Grid container spacing={`${GRID_LAYOUT_GAP}px`} marginTop={`1px`}>
          <Grid item xs={12}>
            <Box height={rowHeight * 3}>
              <PlotMemo
                def={positionInPowerlinePlotDef}
                data={formattedData}
                csvData={csvData}
                entityData={{ entity: "device", ids: deviceIds }}
                qualityData={qualityData}
                isRawData={isRawData}
              />
            </Box>
          </Grid>
          <Grid container item xs={12} spacing={`${GRID_LAYOUT_GAP}px`}>
            {alphaAmplitude && (
              <Grid item xs={12} sm={6} md={3}>
                <Box height={rowHeight}>
                  <PlotMemo
                    def={alphaAmplitudePlotDef}
                    data={[alphaAmplitude]}
                    entityData={{ entity: "device", ids: deviceIds }}
                    isRawData={isRawData}
                  />
                </Box>
              </Grid>
            )}
            {betaAmplitude && (
              <Grid item xs={12} sm={6} md={3}>
                <Box height={rowHeight}>
                  <PlotMemo
                    def={betaAmplitudePlotDef}
                    data={[betaAmplitude]}
                    entityData={{ entity: "device", ids: deviceIds }}
                    isRawData={isRawData}
                  />
                </Box>
              </Grid>
            )}
            {!fp.isNil(alphaDrift?._value) && (
              <Grid item xs={12} sm={6} md={3}>
                <Box height={rowHeight}>
                  <PlotMemo
                    def={alphaDriftPlotDef}
                    data={[alphaDrift]}
                    entityData={{ entity: "device", ids: deviceIds }}
                    isRawData={isRawData}
                  />
                </Box>
              </Grid>
            )}
            {!fp.isNil(betaDrift?._value) && (
              <Grid item xs={12} sm={6} md={3}>
                <Box height={rowHeight}>
                  <PlotMemo
                    def={betaDriftPlotDef}
                    data={[betaDrift]}
                    entityData={{ entity: "device", ids: deviceIds }}
                    isRawData={isRawData}
                  />
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  )
}

export default PowerlineDevicePosition
